import React, {useEffect, useState} from 'react';
import {observer} from 'mobx-react';

import {SelectionEntityModel} from "../../../infrastructure/models/selection-entity.model";
import {UserSelectionClickEvent} from "../../models/events/user-selection-click-event";
import {AnalyticsService} from "../../../third-party-services/analytics/analytics-service";
import {APP} from "../../app/consts";
import {UserSelectionsManager} from "../../managers/user-selections.manager";
import {UseIoC} from "../composers/composer-plugins/use-ioc";
import {DatetimeUtils} from "../../../infrastructure/utils/datetime-utils";
import {DeviceViewModel} from "../../models/device.view-model";
import {Tooltip} from "../tooltip/tooltip";
import {AppTerms} from "../../app/app-terms";
import {AppStore} from "../../app/app-store";
import classNames from "classnames";

export const EntitySelectionComposer = (Component) => UseIoC()(observer((props) => {
  if(props.type === 'AthleteViewModel') {
    return null;
  }
  if(props.disableSelection) {
    return <Component {...props}/>;
  }

  const userSelectionsManager: UserSelectionsManager = props.ioc.getInstance(UserSelectionsManager);
  const datetimeUtils: DatetimeUtils = props.ioc.getInstance(DatetimeUtils);
  const device: DeviceViewModel = props.ioc.getInstance(DeviceViewModel);
  const appStore: DeviceViewModel = props.ioc.getInstance(AppStore);
  const i18n: AppTerms = appStore.state.terms;

  const extraIds: Array<Number> = props.extraIds?.filter((id: Number) => !!id);

  const isSelectionExist = userSelectionsManager.existInSelection(props.id, props.type, extraIds);
  const [active, setActive] = useState(isSelectionExist);

  useEffect(() => {
    const isActive = userSelectionsManager.existInSelection(props.id, props.type, extraIds);

    setActive(isActive);
    props.onChange && props.onChange(isActive);
  }, [userSelectionsManager.userSelections?.length, props.id])

  const addUserSelection = () => {
    const date: String = datetimeUtils.toISO(props.date);
    const selectionModel: SelectionEntityModel = new SelectionEntityModel(props.id, props.type, date, props.source);
    setActive(true);
    props.onChange && props.onChange(true);
    userSelectionsManager.add(selectionModel, props.source);

    createClickEvent(APP.ANALYTICS.USER_SELECTIONS.STATUSES.select);
  }

  const removeUserSelection = () => {
    const selectionModel: SelectionEntityModel = new SelectionEntityModel(props.id, props.type, props.date, props.source);
    setActive(false);
    props.onChange && props.onChange(false);
    userSelectionsManager.remove(selectionModel, props.source);

    createClickEvent(APP.ANALYTICS.USER_SELECTIONS.STATUSES.unselect);
  }

  const onClick = async (e) => {
    e.stopPropagation();
    e.preventDefault();
    props.onClick && await props.onClick(e);

    active ? removeUserSelection() : addUserSelection();
  }

  const createClickEvent = (status: APP.ANALYTICS.USER_SELECTIONS.STATUSES) => {
    const params = {
      entityId: props.id,
      sportTypeId: props.sportTypeId,
      entityType: props.type,
      source: props.source,
      status: status,
      screen: props.screen || APP.ANALYTICS.SCREENS[props.currentLinkModelName],
      sourceScreen: props.sourceScreen,
      screenId: props.currentLinkModelId,
      wizardType: props.wizardType,
      isUserDriven: props.isUserDriven,
      subSource: props.subSource,
      step: props.step,
      placement: props.placementGA || props.source
    }

    const clickEvent: UserSelectionClickEvent = new UserSelectionClickEvent(params);
    const analyticsService: AnalyticsService = props.ioc.getInstance(AnalyticsService);

    analyticsService.createEvent(clickEvent);
  }

  const newProps = {
    i18n,
    device,
    isActive: active,
    ...props,
    onClick: (e) => onClick(e),
    className: classNames(props.className, 'clickable'),
  }

  return (
    <Tooltip
      disable={props.disableSelection || props.disableTooltip}
      title={active ? i18n.REMOVE_SELECTION : i18n.ADD_SELECTION}
      className={props.tooltipClassName}
    >
      <Component {...newProps}/>
    </Tooltip>
  )
}));
